<template>
  <q-card flat bordered>
    <q-card-section class="row items-center justify-between">
      <div>
        <div class="text-h6">Visible Sections</div>
        <div style="font-size: 12px">
          Choose which components to make visible or hidden for everyone in your organization. If
          all components are hidden, the tab will also be hidden.
        </div>
      </div>
      <div class="q-my-auto" v-if="isDirty">
        <q-btn
          :loading="saveChangesLoading"
          label="Save Changes"
          color="primary"
          unelevated
          icon="save"
          no-caps
          @click="saveChanges"
        />
        <q-tooltip v-if="!hasAtLeastOneComponentVisible">
          Please ensure at least one component is visible.
        </q-tooltip>
        <q-tooltip v-else-if="!isDirty"> All changes are up to date. </q-tooltip>
      </div>
    </q-card-section>
    <q-separator />
    <q-list v-if="organizationLoading">
      <q-item v-for="n in 6" :key="n" class="row items-center">
        <q-skeleton animation="blink" type="rect" class="full-width" />
      </q-item>
    </q-list>
    <q-list v-else>
      <q-expansion-item v-for="tab in tabs" :key="tab.name" :label="tab.label" switch-toggle-side>
        <template #header>
          <div class="row items-center q-col-gutter-lg">
            <span style="font-size: 16px; font-weight: 400"> {{ tab.label }} Tab </span>
            <div class="row q-gutter-md">
              <div
                v-if="tab.components.filter((_) => !_.isVisible).length > 0"
                class="row text-grey-6 items-center"
              >
                <q-icon size="xs" name="visibility_off" class="q-mr-sm"></q-icon>
                {{ tab.components.filter((_) => !_.isVisible).length }}
              </div>
              <div
                v-if="tab.components.filter((_) => !_.isVisible).length === tab.components.length"
                class="row text-grey-6 items-center text-caption"
              >
                <q-icon size="xs" name="warning" class="q-mr-xs"></q-icon>
                Tab Hidden
              </div>
            </div>
          </div>
        </template>
        <q-list>
          <q-item v-for="component in tab.components" :key="component.name">
            <div class="full-width">
              <div class="row items-start">
                <div class="q-mr-md">
                  <q-toggle
                    v-model="component.isVisible"
                    checked-icon="check"
                    color="primary"
                    unchecked-icon="clear"
                  />
                </div>
                <div class="col">
                  <span style="font-size: 16px; font-weight: 500">
                    {{ component.label }}
                  </span>
                  <p class="q-ma-none" :class="$q.dark.isActive ? 'text-grey-6' : 'text-grey-8'">
                    {{ component.description }}
                  </p>
                </div>
              </div>
              <q-expansion-item
                v-if="component.columns?.length > 0"
                v-model="component.showColumns"
                dense-toggle
                label="Edit Columns"
                switch-toggle-side
              >
                <q-card flat bordered class="overflow-auto" style="max-height: 400px">
                  <div style="position: sticky; top: 0; z-index: 5; background-color: inherit">
                    <q-card-section>
                      <div class="text-caption text-weight-medium">Visible Columns</div>
                    </q-card-section>
                    <q-separator />
                  </div>
                  <q-list dense>
                    <template v-for="(column, index) in component.columns" :key="column.label">
                      <q-item
                        class="row items-center"
                        draggable="true"
                        @dragstart="startDragging($event, index)"
                        @dragover.prevent="onDragOver(index, component)"
                        @drop="onDrop()"
                      >
                        <q-item-section side>
                          <q-checkbox v-model="column.isVisible" size="sm" />
                        </q-item-section>
                        <q-item-section>
                          <div class="row items-center q-gutter-sm">
                            <div>
                              {{ column.label }}
                            </div>
                            <q-icon name="info_outline">
                              <q-tooltip max-width="200px">
                                {{ column.description }}
                              </q-tooltip>
                            </q-icon>
                          </div>
                        </q-item-section>
                        <q-item-section side style="cursor: move">
                          <q-icon name="menu" />
                        </q-item-section>
                      </q-item>
                    </template>
                  </q-list>
                </q-card>
              </q-expansion-item>
            </div>
          </q-item>
        </q-list>
      </q-expansion-item>
    </q-list>
  </q-card>
</template>

<script lang="ts">
import { useUserStore } from '@/stores/user'
import { applicationTabs, type TabWithVisibleComponents, type VisibleComponent } from '@/utils'
import { getSectionColumn, type OrganizationAttributes } from 'common'
import type { PropType } from 'vue'

export default {
  name: 'OrganizationSections',
  props: {
    organization: {
      type: Object as PropType<OrganizationAttributes>,
      required: true
    },
    saveChangesLoading: {
      type: Boolean,
      required: true
    }
  },
  emits: ['save'],
  data() {
    return {
      tabs: [] as TabWithVisibleComponents[],
      columnDraggedIndex: null as number | null
    }
  },
  computed: {
    organizationLoading() {
      return useUserStore().organizationLoading
    },
    isDirty() {
      return this.tabs.some((tab) =>
        tab.components.some((component) => {
          const isCompDirty =
            (this.organization.sections?.includes(component.name) && !component.isVisible) ||
            (!this.organization.sections?.includes(component.name) && component.isVisible)
          if (isCompDirty) {
            return true
          }
          return (
            this.organization?.columns?.toString() !== this.generateVisibleColumnsArray().toString()
          )
        })
      )
    },
    hasAtLeastOneComponentVisible() {
      return this.tabs.some((tab) => tab.components.some((_) => _.isVisible))
    }
  },
  mounted() {
    if (!this.organization.sections?.length) {
      this.$q.notify({
        message: 'Error loading organization settings, please try again later.',
        color: 'negative',
        position: 'bottom'
      })
    }
    const tempTabs = applicationTabs.filter((tab) => {
      if (tab.featureFlag) {
        return useUserStore().hasFeatureFlag(null, tab.featureFlag)
      }
      return true
    })
    const org = this.organization
    tempTabs.forEach((tab) => {
      tab.components.forEach((component) => {
        component['isVisible'] = org.sections.includes(component.name)
        if (component.columns && org.columns) {
          const orgColumnOrder = new Map(org.columns.map((column, index) => [column, index]))

          component.columns.forEach((column) => {
            const columnName = this.getSectionColumn(component.name, column.name)
            column['isVisible'] = orgColumnOrder.has(columnName)
          })

          component.columns.sort((a, b) => {
            const indexA = orgColumnOrder.get(this.getSectionColumn(component.name, a.name))
            const indexB = orgColumnOrder.get(this.getSectionColumn(component.name, b.name))

            // handle columns not found in the organization columns
            if (indexA === undefined) return 1
            if (indexB === undefined) return -1

            return indexA - indexB
          })
        }
      })
    })
    this.tabs = tempTabs as TabWithVisibleComponents[]
  },
  methods: {
    getSectionColumn,
    saveChanges() {
      const visibleSections = this.tabs.flatMap((tab) =>
        tab.components.filter((comp) => comp.isVisible).map((_) => _.name)
      )
      const visibleColumns = this.tabs.flatMap((tab) =>
        tab.components.flatMap(
          (component) =>
            component.columns
              ?.filter((column) => column.isVisible)
              .map((column) => this.getSectionColumn(component.name, column.name)) || []
        )
      )
      this.$emit('save', visibleSections, visibleColumns)
    },
    startDragging(event: DragEvent, index: number) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.dropEffect = 'move'
      this.columnDraggedIndex = index
    },
    onDragOver(targetIndex: number, component: VisibleComponent) {
      if (this.columnDraggedIndex !== null) {
        if (targetIndex > this.columnDraggedIndex) {
          // Remove the column being dragged and store it in a temporary variable
          const draggedColumn = component.columns.splice(this.columnDraggedIndex, 1)[0]
          this.columnDraggedIndex += 1
          // Insert the dragged column at the new index
          component.columns.splice(this.columnDraggedIndex, 0, draggedColumn)
        } else if (targetIndex < this.columnDraggedIndex && this.columnDraggedIndex > 0) {
          const draggedColumn = component.columns.splice(this.columnDraggedIndex, 1)[0]
          this.columnDraggedIndex -= 1
          component.columns.splice(this.columnDraggedIndex, 0, draggedColumn)
        }
      }
    },
    onDrop() {
      this.columnDraggedIndex = null
    },
    generateVisibleColumnsArray() {
      return this.tabs.flatMap((tab) =>
        tab.components.flatMap(
          (component) =>
            component.columns
              ?.filter((column) => column.isVisible)
              .map((column) => this.getSectionColumn(component.name, column.name)) || []
        )
      )
    }
  }
}
</script>
